import React from 'react';
import PropTypes from 'prop-types';
import { Rectangle, Typography, Shadow, FlexContainer, Cushion, Space } from '@pitchero/react-ui';
import { map, entries, pickBy, capitalize, sumBy, values } from 'lodash';
import { Trans } from '../../../lib/i18n';
import IconWithTheme from '../../icon-with-theme';

const StatsCard = ({ titleI18nKey, stats: statsProp, dismissals, IconComponent }) => {
  const { average_runs: averageRuns, average, ...stats } = statsProp;
  const averageValue = averageRuns || average;
  const statsWithValues = entries(pickBy(stats));
  const dismissalsWithValues = entries(pickBy(dismissals, 'count'));

  return (
    <Shadow>
      <Rectangle fill="white" radius="rounded" component="div" style={{ overflow: 'hidden' }}>
        <FlexContainer
          flexDirection="column"
          justifyContent="space-between"
          style={{ height: '100%' }}
        >
          <Rectangle fill="alabaster">
            <Cushion horizontal="medium" vertical="small">
              <FlexContainer justifyContent="space-between" flexWrap="wrap">
                <div>
                  <Space right="xsmall">
                    <IconWithTheme>
                      <IconComponent size={22} style={{ verticalAlign: 'middle' }} />
                    </IconWithTheme>
                  </Space>
                  <Typography preset="tag--large" style={{ lineHeight: '24px' }}>
                    <Trans i18nKey={titleI18nKey}>{titleI18nKey}</Trans>
                  </Typography>
                </div>
                <div>
                  {averageValue && (
                    <>
                      <Space right="small">
                        <Typography preset="tag--small" color="dustygrey">
                          <Trans i18nKey="team:average">Average</Trans>
                        </Typography>
                      </Space>
                      <Typography
                        preset="staticSubtitle--large"
                        style={{ verticalAlign: 'middle' }}
                      >
                        {averageValue}
                      </Typography>
                    </>
                  )}

                  {!averageValue && (
                    <>
                      <Space right="small">
                        <Typography preset="tag--small" color="dustygrey">
                          <Trans i18nKey="team:dismissals">Dismissals</Trans>
                        </Typography>
                      </Space>
                      <Typography
                        preset="staticSubtitle--large"
                        style={{ verticalAlign: 'middle' }}
                      >
                        {sumBy(values(dismissals), 'count')}
                      </Typography>
                    </>
                  )}
                </div>
              </FlexContainer>
            </Cushion>
          </Rectangle>

          <Cushion all="medium" style={{ flex: 1 }}>
            <FlexContainer
              justifyContent="center"
              flexWrap="wrap"
              style={{ alignContent: 'flex-start', minHeight: '125px' }}
            >
              {map(statsWithValues, ([key, value]) => (
                <Space all="xsmall" responsive={[{ minWidth: 'tab', props: { all: 'small' } }]}>
                  <FlexContainer
                    style={{ textAlign: 'center' }}
                    flexDirection="column"
                    justifyContent="flex-bottom"
                  >
                    <Space bottom="xsmall">
                      <Typography preset="tag--small" color="dustygrey">
                        <Trans i18nKey={`teams:player_profile_stats_${key}`}>{key}</Trans>
                      </Typography>
                    </Space>
                    <Rectangle
                      fill="alabaster"
                      radius="circle"
                      component="div"
                      style={{ width: '60px', height: '60px', margin: '0 auto' }}
                    >
                      <Typography lineHeight="60px" preset="staticSubtitle--large">
                        {value}
                      </Typography>
                    </Rectangle>
                  </FlexContainer>
                </Space>
              ))}
            </FlexContainer>
          </Cushion>

          <Space all="medium" top="0">
            <Rectangle
              radius="rounded"
              style={{ height: '24px', overflow: 'hidden' }}
              component="div"
            >
              {map(dismissalsWithValues, ([key, dismissal]) => (
                <Rectangle
                  fill={`dismissals${capitalize(key)}`}
                  style={{
                    width: `${dismissal.percent}%`,
                    height: '24px',
                    display: 'inline-block',
                  }}
                />
              ))}
            </Rectangle>
            <Space top="small">
              <Space right="small">
                <Typography
                  preset="tag--small"
                  lineHeight="12px"
                  style={{ verticalAlign: 'middle' }}
                >
                  <Trans i18nKey="team:dismissals">Dismissals</Trans>
                </Typography>
              </Space>
              {map(dismissalsWithValues, ([key, dismissal]) => (
                <Space right="small" style={{ display: 'inline-block' }}>
                  <Space right="xxsmall">
                    <Rectangle
                      radius="rounded"
                      fill={`dismissals${capitalize(key)}`}
                      style={{
                        width: '12px',
                        height: '12px',
                        display: 'inline-block',
                      }}
                    />
                  </Space>
                  <Typography
                    preset="tag--small"
                    color="dustygrey"
                    lineHeight="12px"
                    style={{ verticalAlign: 'middle' }}
                  >
                    <Trans i18nKey={`teams:player_profile_stats_${key}`}>{key}</Trans>
                    {` (${dismissal.count})`}
                  </Typography>
                </Space>
              ))}
            </Space>
          </Space>
        </FlexContainer>
      </Rectangle>
    </Shadow>
  );
};

StatsCard.propTypes = {
  titleI18nKey: PropTypes.string.isRequired,
  stats: PropTypes.shape().isRequired,
  dismissals: PropTypes.shape().isRequired,
  IconComponent: PropTypes.string.isRequired,
};

export default StatsCard;
